/**
 * Tipos y etiquetas de perfil de negocio — seguro para el cliente (sin DB ni JWT).
 */

export type BusinessProfile =
  | "catalog"
  | "fast_food"
  | "market"
  | "dietetics"
  | "restaurant"
  | "pharmacy"
  | "fashion"
  | "bazar"
  | "digital_products"
  | "pet"
  | "cafe"
  | "servicios";

export const BUSINESS_PROFILE_LABELS: Record<BusinessProfile, string> = {
  catalog: "Catálogo simple",
  fast_food: "Fast food / Hamburguesería",
  market: "Almacén / Mercado",
  dietetics: "Dietética / Natural",
  restaurant: "Restaurante / Rotisería",
  pharmacy: "Farmacia / Salud",
  fashion: "Indumentaria / Moda",
  bazar: "Bazar & Hogar",
  digital_products: "Productos digitales",
  pet: "Pet shop",
  cafe: "Cafetería / Panadería",
  servicios: "Servicios / Turnos",
};

/**
 * Perfiles gastronómicos — habilitan la impresión de COMANDA de cocina/barra.
 * El resto de los rubros solo imprime tickets.
 */
const FOOD_PROFILES: ReadonlySet<BusinessProfile> = new Set<BusinessProfile>([
  "fast_food",
  "restaurant",
  "cafe",
]);

/**
 * Rubros alimenticios / food-market: ocultan copy de moda (estampa/bordado)
 * y tratan extras como guarniciones/toppings.
 */
const FOOD_MARKET_PROFILES: ReadonlySet<BusinessProfile> = new Set<BusinessProfile>([
  "fast_food",
  "restaurant",
  "cafe",
  "market",
  "dietetics",
]);

/**
 * Perfiles de retail — habilitan el TICKET DE CAMBIO (sin precios).
 */
const RETAIL_PROFILES: ReadonlySet<BusinessProfile> = new Set<BusinessProfile>([
  "fashion",
  "bazar",
  "pet",
]);

/**
 * Personalización Merchant (estampados/bordados) — solo indumentaria/moda.
 * El flag de plan `integrations_merchant` no alcanza: debe coincidir el nicho.
 */
const MERCHANT_PERSONALIZATION_PROFILES: ReadonlySet<BusinessProfile> = new Set<BusinessProfile>([
  "fashion",
]);

/**
 * SCRUM-416 — Guía de talles: capacidad comercial de indumentaria, no una
 * configuración genérica de catálogo. Deliberadamente separada de
 * MERCHANT_PERSONALIZATION_PROFILES aunque hoy coincida el mismo set: son
 * capacidades distintas (talles vs. estampado/bordado) que podrían divergir.
 * Sin evidencia de un caso de negocio real para bazar/pet/dietetics, no se
 * asume su aplicabilidad — ver ticket: "no asumir por defecto".
 */
const SIZE_GUIDE_PROFILES: ReadonlySet<BusinessProfile> = new Set<BusinessProfile>([
  "fashion",
]);

function normalizeProfile(profile?: string | null): BusinessProfile {
  if (profile && profile in BUSINESS_PROFILE_LABELS) return profile as BusinessProfile;
  return "catalog";
}

/** ¿Este negocio puede imprimir comanda de cocina? (gastronomía) */
export function businessAllowsKitchenTicket(profile?: string | null): boolean {
  return FOOD_PROFILES.has(normalizeProfile(profile));
}

/** ¿Este negocio puede imprimir ticket de cambio? (indumentaria / bazar) */
export function businessAllowsExchangeTicket(profile?: string | null): boolean {
  return RETAIL_PROFILES.has(normalizeProfile(profile));
}

/** ¿Rubro alimenticio (comida / mercado / dietética)? */
export function businessIsFoodMarket(profile?: string | null): boolean {
  return FOOD_MARKET_PROFILES.has(normalizeProfile(profile));
}

/** Nicho turnos/profesionales (absorción de Bookings Hub, SCRUM-266). */
export function businessIsServicesNiche(profile?: string | null): boolean {
  return normalizeProfile(profile) === "servicios";
}

/**
 * ¿Puede usar el módulo Merchant de personalización (estampa/bordado)?
 * Requiere nicho moda; el caller también debe chequear `integrations_merchant`.
 */
/* vite-cache-bust: merchant personalization helper */
export function businessAllowsMerchantPersonalization(profile?: string | null): boolean {
  return MERCHANT_PERSONALIZATION_PROFILES.has(normalizeProfile(profile));
}

/** ¿Aplica Guía de talles a este nicho? (SCRUM-416 — capacidad de indumentaria, no genérica) */
export function businessAllowsSizeGuide(profile?: string | null): boolean {
  return SIZE_GUIDE_PROFILES.has(normalizeProfile(profile));
}


/** Tipos de sucursal / punto operativo. */
export type BranchTypeKey =
  | "store"
  | "pickup"
  | "dark_store"
  | "dark_kitchen"
  | "warehouse"
  | "distribution_center";

/**
 * Dark Kitchen solo en gastronomía (cocina). Retail / bebidas / moda no lo ven.
 * Dark Store / depósito sí aplican a retail con delivery.
 */
export function branchTypesForBusinessProfile(profile?: string | null): BranchTypeKey[] {
  const p = normalizeProfile(profile);
  if (p === "digital_products") {
    return ["store", "pickup"];
  }
  const types: BranchTypeKey[] = ["store", "pickup", "dark_store", "warehouse", "distribution_center"];
  if (FOOD_PROFILES.has(p)) {
    types.push("dark_kitchen");
  }
  return types;
}


export const BRANCH_TYPE_LABELS: Record<BranchTypeKey, string> = {
  store: "Local de venta",
  pickup: "Punto de retiro",
  dark_store: "Dark Store",
  dark_kitchen: "Dark Kitchen",
  warehouse: "Depósito",
  distribution_center: "Centro de distribución",
};

export const BRANCH_TYPE_DESCRIPTIONS: Record<BranchTypeKey, string> = {
  store: "Local con atención al público y venta directa.",
  pickup: "Punto de retiro sin atención permanente.",
  dark_store: "Almacén interno para pedidos de delivery.",
  dark_kitchen: "Cocina ghost sin salón ni atención al público.",
  warehouse: "Depósito general de mercadería.",
  distribution_center: "Centro de distribución a otras sucursales.",
};
